/**
 * Customers List Page
 * Status change confirmations + filter panel UI helpers + customer-autocomplete picker.
 * Filtering, sorting, and pagination are handled declaratively by HTMX.
 *
 * The customer search uses the same SearchPicker component as the Jobs and
 * Visits list pages so the filter row stays consistent across the office.
 * Picking a customer narrows the list to that single customer (the page IS
 * the customer list, so the filter is essentially a typed shortcut to the
 * detail row).
 *
 * Pause UX (ON_HOLD): opens the canonical centered modal driven by
 * createDynamicModal (start date / end date / reason). The captured metadata
 * is stored on the Customer and propagated by the signal layer to every
 * paused child Job.
 *
 * Deactivate UX (INACTIVE): opens the canonical centered modal driven by
 * createDynamicModal (single required reason). The reason is stored on the
 * Customer and propagated by the signal layer to every cancelled child Job's
 * cancellation_reason.
 *
 * Both flows previously lived in a right-side drawer; they were migrated to
 * modals because (a) the project's form-modals/form-drawers rules say
 * "≤ 3-4 fields, fire-and-forget → modal", (b) destructive-ish decisions are
 * the textbook modal use-case (Stripe / Linear / GitHub all do the same),
 * and (c) it lets us delete the bespoke _customer_drawer.html shell.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';

const state = {
  translations: {},
  urls: {
    pauseDrawerUrl: '/office/customers/{id}/pause/drawer/',
    deactivateDrawerUrl: '/office/customers/{id}/deactivate/drawer/',
  },
};

let customerPicker = null;

// Module-level helpers for the two status-change modals. They're created
// lazily on first need (after the shells are present in the DOM) and
// re-created on every page-level init() so a stale reference to a
// detached modal node from a previous boost cycle never sticks around —
// see form-modals.mdc §9 ("Boosted-nav module caches — invalidate on
// re-init") for the long-form rationale.
let pauseModal = null;
let deactivateModal = null;

// Module-level idempotency: listeners on document / document.body must
// be bound ONCE for the SPA lifetime. See pages/invoices.js for a fuller
// rationale — hx-boost re-runs page init, which would stack listeners.
let afterSettleBound = false;

// Single delegated click for [data-customers-action] markers (Phase 6.C
// + 6.E). Bound once for the SPA lifetime. Catches both customer-list
// row buttons (status change + delete) and customer-detail page header
// buttons (same UX as the list). The handler reads its config from
// data-* attrs so the same delegate serves all customer pages.
let actionDelegateBound = false;

export function initCustomerList(options = {}) {
  const { hasActiveFilters = false, urls = {} } = options;
  state.translations = options.translations || {};
  // Templated URLs let the same JS bundle work for both the list page
  // (customer ids resolved at click time) and the detail page (single
  // customer in the page header). The {id} placeholder is replaced when
  // confirmStatusChange routes to the modal.
  state.urls = {
    pauseDrawerUrl: urls.pauseDrawerUrl || '/office/customers/{id}/pause/drawer/',
    deactivateDrawerUrl: urls.deactivateDrawerUrl || '/office/customers/{id}/deactivate/drawer/',
  };

  // Reset the cached helpers so a re-init after an hx-boost swap binds to
  // the fresh modal shells in the new DOM, not to the orphaned ones from
  // the previous page life.
  pauseModal = null;
  deactivateModal = null;

  window.confirmCustomerStatusChange = function (customerName, customerId, targetStatus) {
    confirmStatusChange(customerName, customerId, targetStatus, state.translations);
  };

  initFilterPanelUI(hasActiveFilters);
  initCustomerPicker(urls);
  bindAfterSettleOnce();
  bindActionDelegationOnce();
}

/**
 * Auto-init entry point (wired via data-auto-init="customerList" in the
 * template). Reads configuration from data-* attrs so the template stays
 * script-free. Called by _runAutoInit in app.js on htmx:afterSwap and
 * htmx:historyRestore.
 */
export function autoInitCustomerList() {
  const root = document.querySelector('[data-auto-init="customerList"]');
  if (!root) return;

  const ds = root.dataset;
  initCustomerList({
    hasActiveFilters: ds.hasActiveFilters === '1',
    urls: {
      customerSearch: ds.customerSearchUrl || '/office/api/customers/search/',
    },
    translations: {
      activate: ds.trActivate || 'Activate',
      activateMessage: ds.trActivateMessage || 'This will set the customer status to Active.',
      yesActivate: ds.trYesActivate || 'Yes, Activate',
      pauseTitle: ds.trPauseTitle || 'Pause Service',
      deactivateTitle: ds.trDeactivateTitle || 'Deactivate Customer',
      errorLoadingPauseForm: ds.trErrorPause || 'Error loading pause form',
      errorLoadingDeactivateForm: ds.trErrorDeactivate || 'Error loading deactivate form',
    },
  });
}

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function (e) {
    const target = e.detail && e.detail.target;
    if (!target || target.id !== 'list-results') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

// ========================================================
// Delegated click — customer status-change + delete (Phase 6.C + 6.E)
// ========================================================
//
// Markers (used by both customers/list.html and customers/detail.html):
//   - [data-customers-action="status-change"]
//       data-customer-id, data-customer-name, data-target-status
//     → confirmCustomerStatusChange(name, id, targetStatus)
//   - [data-customers-action="delete"]
//       data-customer-delete-form-id
//       data-confirm-title, data-confirm-message, data-confirm-text
//     → showConfirmModal({ ... onConfirm: form.submit() })
//
// Bound ONCE per SPA lifetime via actionDelegateBound. Always lives on
// document.body so it survives both inner #list-results swaps AND
// hx-boost #main-content swaps.

function handleCustomerActionClick(evt) {
  const t = evt.target;
  if (!t || typeof t.closest !== 'function') return;
  const btn = t.closest('[data-customers-action]');
  if (!btn) return;

  const action = btn.dataset.customersAction;

  if (action === 'status-change') {
    const id = btn.dataset.customerId;
    const name = btn.dataset.customerName || '';
    const targetStatus = btn.dataset.targetStatus;
    if (!id || !targetStatus) return;
    confirmStatusChange(name, id, targetStatus, state.translations);
    return;
  }

  if (action === 'delete') {
    const formId = btn.dataset.customerDeleteFormId;
    if (!formId || typeof window.showConfirmModal !== 'function') return;
    const ds = btn.dataset;
    window.showConfirmModal({
      title: ds.confirmTitle || 'Delete?',
      message: ds.confirmMessage || '',
      confirmText: ds.confirmText || 'Delete',
      type: 'danger',
      onConfirm: function () {
        const form = document.getElementById(formId);
        if (form) form.submit();
      },
    });
  }
}

function bindActionDelegationOnce() {
  if (actionDelegateBound) return;
  actionDelegateBound = true;
  document.body.addEventListener('click', handleCustomerActionClick);
}

function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('customerFilterForm');

  // ``initCustomerList`` is also called from ``customer-detail.js`` so the
  // Pause / Activate / Deactivate icons in the detail page header keep
  // working. The customer-detail Worksites tab template happens to share
  // the ``#filter-panel`` / ``#toggle-filters-btn`` ids (it has its own
  // collapsible filter panel for archived worksites) but uses a different
  // form id — ``#worksitesFilterForm``. ``customer-detail.js`` already
  // owns that toggle (see ``initFilterPanel`` there with
  // ``_custDetailFilterBound``); if we *also* bound it here we'd get two
  // click handlers on the same button → toggle twice → "filter button
  // does nothing". Gate every binding behind the customer-list-specific
  // form so this function is a strict no-op on the detail page.
  if (!form) return;

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency via a plain JS property on the element.
  // We deliberately avoid dataset.* because HTMX's hx-boost history
  // snapshots DOM via outerHTML — dataset survives a snapshot/restore
  // cycle while the attached listener does NOT, which would cause the
  // toggle to silently stop working on back navigation. A plain
  // property (not a data-attribute) is invisible to outerHTML so we
  // re-bind correctly on every restore.
  if (toggleBtn && panel && !toggleBtn._custFilterBound) {
    toggleBtn._custFilterBound = true;
    toggleBtn.addEventListener('click', function () {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._custResetBound) {
    resetBtn._custResetBound = true;
    resetBtn.addEventListener('click', function () {
      form.querySelectorAll('select').forEach(function (sel) {
        sel.value = '';
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper && wrapper.querySelector('.custom-select-text');
        if (textEl && sel.options[0]) textEl.textContent = sel.options[0].text;
      });

      form.querySelectorAll('input[type="text"]').forEach(function (inp) {
        inp.value = '';
      });

      form.querySelectorAll('input[type="hidden"]').forEach(function (inp) {
        inp.value = '';
      });

      if (customerPicker) customerPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

function initCustomerPicker(urls) {
  const customerInput = document.getElementById('customer-search');
  if (!customerInput) return;

  customerPicker = new SearchPicker({
    inputId: 'customer-search',
    hiddenInputId: 'customer-filter',
    dropdownId: 'customer-search-results',
    endpoint: ((urls && urls.customerSearch) || '/office/api/customers/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
    isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
    onSelect: function () {
      const form = document.getElementById('customerFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: function () {
      const form = document.getElementById('customerFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

// renderCustomerItem moved to ``frontend/src/utils/ui/customer-picker.js``.

function confirmStatusChange(customerName, customerId, targetStatus, translations) {
  // ON_HOLD opens the dynamic Pause Service modal — captures dates + reason
  // and propagates them to every active child Job via signals.
  if (targetStatus === 'on_hold') {
    var pauseHelper = ensurePauseModalHelper(translations);
    if (!pauseHelper) return;
    pauseHelper.open(
      state.urls.pauseDrawerUrl.replace('{id}', String(customerId)),
      translations.pauseTitle || 'Pause Service',
    );
    return;
  }

  // INACTIVE opens the dynamic Deactivate Customer modal — captures the
  // required reason and propagates it to every cancelled child Job.
  if (targetStatus === 'inactive') {
    var deactivateHelper = ensureDeactivateModalHelper(translations);
    if (!deactivateHelper) return;
    deactivateHelper.open(
      state.urls.deactivateDrawerUrl.replace('{id}', String(customerId)),
      translations.deactivateTitle || 'Deactivate Customer',
    );
    return;
  }

  // Remaining path: activate (one-click confirm dialog, no extra fields).
  var formId = 'toggle-form-cust-' + customerId;
  var targetStatusInput = document.getElementById('target-status-' + customerId);
  var title = (translations.activate || 'Activate') + ' ' + customerName + '?';
  var message = translations.activateMessage || 'This will set the customer status to Active.';
  var confirmText = translations.yesActivate || 'Yes, Activate';

  if (typeof window.showConfirmModal === 'function') {
    window.showConfirmModal({
      title: title,
      message: message,
      confirmText: confirmText,
      type: 'success',
      onConfirm: function () {
        if (targetStatusInput) targetStatusInput.value = targetStatus;
        var form = document.getElementById(formId);
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      },
    });
  } else if (confirm(message)) {
    if (targetStatusInput) targetStatusInput.value = targetStatus;
    var fallbackForm = document.getElementById(formId);
    if (fallbackForm) fallbackForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Pause Service modal (ON_HOLD)
// ───────────────────────────────────────────────────────────────────────────
//
// Canonical form-modal pattern (form-modals.mdc):
//   - Static shell: #customer-pause-modal (see _status_change_modal_shells.html)
//   - GET endpoint: customer_pause_drawer → _pause_form_body.html
//   - POST endpoint: customer_pause_submit → JSON {success: true} or 422 + body
//   - Validation re-render: server returns the form body with bound errors and
//     status=422; the helper paints it back into .modal-content.
//   - Success: helper closes the modal, default onSuccess triggers
//     window._softReload() to refresh the list/detail with the new status.

function ensurePauseModalHelper(translations) {
  if (pauseModal) return pauseModal;
  pauseModal = createDynamicModal({
    modalId: 'customer-pause-modal',
    onContentInjected: initFormWidgets,
    errorLoadingMessage: translations.errorLoadingPauseForm || 'Error loading pause form',
  });
  return pauseModal;
}

// ───────────────────────────────────────────────────────────────────────────
// Deactivate Customer modal (INACTIVE)
// ───────────────────────────────────────────────────────────────────────────
//
// Same anatomy as the pause modal above. The form body has a single required
// `deactivation_reason` textarea plus a contextual impact card (red when
// there are active jobs / future visits, blue when there's nothing to
// cascade). No widgets to re-init beyond Flowbite, but we route through the
// same hook for consistency.

function ensureDeactivateModalHelper(translations) {
  if (deactivateModal) return deactivateModal;
  deactivateModal = createDynamicModal({
    modalId: 'customer-deactivate-modal',
    onContentInjected: initFormWidgets,
    errorLoadingMessage: translations.errorLoadingDeactivateForm || 'Error loading deactivate form',
  });
  return deactivateModal;
}

function initFormWidgets(content) {
  // Re-attach Flowbite Datepicker to any [datepicker]-decorated input the
  // server just rendered. Each input keeps its own instance so a 422
  // re-render gets a fresh widget rather than the previous one bound to a
  // detached element. The early `new` guard makes the call idempotent for
  // the case where Flowbite already wired the input via auto-init.
  if (typeof window.Datepicker !== 'undefined' && content) {
    content.querySelectorAll('[datepicker]').forEach(function (el) {
      if (!el._datepicker) {
        el._datepicker = new window.Datepicker(el, { format: 'dd/mm/yyyy', autohide: true });
      }
    });
  }
  if (typeof window.reinitFlowbiteWidgets === 'function') {
    window.reinitFlowbiteWidgets(content);
  }
}

window.initCustomerList = initCustomerList;
// Note: do NOT write to window.App.pages here. app.js assigns it to the
// ES module namespace of ./pages/index.js, which is sealed (read-only
// getters). Mutating it throws in strict mode. `initCustomerList` is
// already reachable as `App.pages.initCustomerList` because
// pages/index.js re-exports it — templates can keep calling
// `App.pages.initCustomerList(options)` directly.
